/**
 * AI Sales Assistant Component
 *
 * Provides AI-powered tools for vehicle descriptions, listing titles,
 * deal suggestions, and lead scoring.
 */

import {
  ArrowPathIcon,
  CalculatorIcon,
  DocumentTextIcon,
  SparklesIcon,
  TagIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import apiService from '../../services/api';
import { VehicleDescriptionStructured } from '../vehicles/buildDescriptionHtml';

interface AISalesAssistantProps {
  vehicleId?: string;
  customerId?: string;
  vehiclePrice?: number;
  creditScore?: number;
}

interface GeneratedContent {
  description?: VehicleDescriptionStructured;
  title?: string;
  dealSuggestion?: any;
  leadScore?: any;
}

const AISalesAssistant: React.FC<AISalesAssistantProps> = ({
  vehicleId,
  customerId,
  vehiclePrice,
  creditScore,
}) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [content, setContent] = useState<GeneratedContent>({});
  const [error, setError] = useState<string | null>(null);

  const [descriptionStyle, setDescriptionStyle] = useState('professional');
  const [descriptionLength, setDescriptionLength] = useState('medium');
  const [platform, setPlatform] = useState('craigslist');

  const generateDescription = async () => {
    if (!vehicleId) return;

    setLoading('description');
    setError(null);

    try {
      const response = await apiService.post('/ai/generate-description', {
        vehicle_id: vehicleId,
        style: descriptionStyle,
        length: descriptionLength,
      });

      setContent((prev) => ({ ...prev, description: response as VehicleDescriptionStructured }));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate description');
    } finally {
      setLoading(null);
    }
  };

  const generateTitle = async () => {
    if (!vehicleId) return;

    setLoading('title');
    setError(null);

    try {
      const response = await apiService.post('/ai/generate-title', {
        vehicle_id: vehicleId,
        platform: platform,
        include_emoji: platform === 'facebook',
      });

      setContent((prev) => ({ ...prev, title: (response as any).title }));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate title');
    } finally {
      setLoading(null);
    }
  };

  const suggestDeal = async () => {
    if (!vehiclePrice) return;

    setLoading('deal');
    setError(null);

    try {
      const response = await apiService.post('/ai/suggest-deal', {
        vehicle_price: vehiclePrice,
        customer_credit_score: creditScore || null,
        down_payment: null,
        trade_in_value: null,
      });

      setContent((prev) => ({ ...prev, dealSuggestion: response }));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate deal suggestion');
    } finally {
      setLoading(null);
    }
  };

  const scoreLead = async () => {
    if (!customerId) return;

    setLoading('lead');
    setError(null);

    try {
      const response = await apiService.post('/ai/score-lead', {
        customer_id: customerId,
        notes: null,
      });

      setContent((prev) => ({ ...prev, leadScore: response }));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to score lead');
    } finally {
      setLoading(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
      <div className="flex items-center space-x-2">
        <SparklesIcon className="h-5 w-5 text-purple-600" />
        <h2 className="text-lg font-semibold text-gray-900">AI Sales Assistant</h2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Vehicle Description Generator */}
      {vehicleId && (
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center space-x-2">
            <DocumentTextIcon className="h-4 w-4 text-blue-600" />
            <h3 className="font-medium text-gray-900">Generate Description</h3>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Style</label>
              <select
                value={descriptionStyle}
                onChange={(e) => setDescriptionStyle(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="luxury">Luxury</option>
                <option value="budget">Budget-Friendly</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Length</label>
              <select
                value={descriptionLength}
                onChange={(e) => setDescriptionLength(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="short">Short (50-75 words)</option>
                <option value="medium">Medium (100-150 words)</option>
                <option value="long">Long (200-250 words)</option>
              </select>
            </div>
          </div>

          <button
            onClick={generateDescription}
            disabled={loading === 'description'}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading === 'description' ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <SparklesIcon className="h-4 w-4" />
                <span>Generate Description</span>
              </>
            )}
          </button>

          {content.description && (
            <div className="bg-gray-50 rounded-md p-3 space-y-2">
              {content.description.fallback && (
                <p className="text-xs text-amber-600 mb-2">
                  ℹ️ AI not configured — generated locally. Add an API key in Settings →
                  Integrations.
                </p>
              )}
              <p className="text-sm text-gray-700">{content.description.intro_paragraph}</p>
              <ul className="text-sm text-gray-700 list-none space-y-1 mt-2">
                {content.description.highlights.map((h, i) => (
                  <li key={i}>
                    {h.icon} <strong>{h.title}:</strong> {h.text}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-600 italic mt-2">
                {content.description.closing_paragraph}
              </p>
              <button
                onClick={() =>
                  copyToClipboard(
                    [
                      content.description!.intro_paragraph,
                      content
                        .description!.highlights.map((h) => `${h.icon} ${h.title}: ${h.text}`)
                        .join('\n'),
                      content.description!.closing_paragraph,
                    ].join('\n\n')
                  )
                }
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Copy to Clipboard
              </button>
            </div>
          )}
        </div>
      )}

      {/* Listing Title Generator */}
      {vehicleId && (
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center space-x-2">
            <TagIcon className="h-4 w-4 text-green-600" />
            <h3 className="font-medium text-gray-900">Generate Listing Title</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
            >
              <option value="craigslist">Craigslist</option>
              <option value="facebook">Facebook Marketplace</option>
              <option value="offerup">OfferUp</option>
            </select>
          </div>

          <button
            onClick={generateTitle}
            disabled={loading === 'title'}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading === 'title' ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <SparklesIcon className="h-4 w-4" />
                <span>Generate Title</span>
              </>
            )}
          </button>

          {content.title && (
            <div className="bg-gray-50 rounded-md p-3 space-y-2">
              <p className="text-sm text-gray-700 font-medium">{content.title}</p>
              <button
                onClick={() => copyToClipboard(content.title!)}
                className="text-xs text-green-600 hover:text-green-700 font-medium"
              >
                Copy to Clipboard
              </button>
            </div>
          )}
        </div>
      )}

      {/* Deal Structure Advisor */}
      {vehiclePrice && (
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center space-x-2">
            <CalculatorIcon className="h-4 w-4 text-purple-600" />
            <h3 className="font-medium text-gray-900">Deal Structure Suggestion</h3>
          </div>

          <button
            onClick={suggestDeal}
            disabled={loading === 'deal'}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading === 'deal' ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <SparklesIcon className="h-4 w-4" />
                <span>Suggest Deal Structure</span>
              </>
            )}
          </button>

          {content.dealSuggestion && (
            <div className="bg-gray-50 rounded-md p-3 space-y-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {content.dealSuggestion.recommended_down_payment && (
                  <div>
                    <p className="text-gray-600">Down Payment</p>
                    <p className="font-medium text-gray-900">
                      {content.dealSuggestion.recommended_down_payment}
                    </p>
                  </div>
                )}
                {content.dealSuggestion.suggested_apr_range && (
                  <div>
                    <p className="text-gray-600">APR Range</p>
                    <p className="font-medium text-gray-900">
                      {content.dealSuggestion.suggested_apr_range}
                    </p>
                  </div>
                )}
                {content.dealSuggestion.recommended_term && (
                  <div>
                    <p className="text-gray-600">Term</p>
                    <p className="font-medium text-gray-900">
                      {content.dealSuggestion.recommended_term}
                    </p>
                  </div>
                )}
                {content.dealSuggestion.risk_assessment && (
                  <div>
                    <p className="text-gray-600">Risk Level</p>
                    <p className="font-medium text-gray-900">
                      {content.dealSuggestion.risk_assessment}
                    </p>
                  </div>
                )}
              </div>
              {content.dealSuggestion.tips && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-700 mb-2">Tips:</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {content.dealSuggestion.tips.map((tip: string, idx: number) => (
                      <li key={idx} className="flex items-start">
                        <span className="mr-1">•</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Lead Quality Scorer */}
      {customerId && (
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center space-x-2">
            <UserGroupIcon className="h-4 w-4 text-orange-600" />
            <h3 className="font-medium text-gray-900">Lead Quality Score</h3>
          </div>

          <button
            onClick={scoreLead}
            disabled={loading === 'lead'}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading === 'lead' ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span>Scoring...</span>
              </>
            ) : (
              <>
                <SparklesIcon className="h-4 w-4" />
                <span>Score Lead Quality</span>
              </>
            )}
          </button>

          {content.leadScore && (
            <div className="bg-gray-50 rounded-md p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">Lead Score</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {content.leadScore.score || 'N/A'}
                  </p>
                </div>
                {content.leadScore.priority && (
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      content.leadScore.priority === 'Hot'
                        ? 'bg-red-100 text-red-700'
                        : content.leadScore.priority === 'Warm'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {content.leadScore.priority}
                  </span>
                )}
              </div>

              {content.leadScore.recommended_action && (
                <div>
                  <p className="text-xs font-medium text-gray-700">Next Action:</p>
                  <p className="text-sm text-gray-600">{content.leadScore.recommended_action}</p>
                </div>
              )}

              {content.leadScore.best_follow_up_time && (
                <div>
                  <p className="text-xs font-medium text-gray-700">Best Time to Contact:</p>
                  <p className="text-sm text-gray-600">{content.leadScore.best_follow_up_time}</p>
                </div>
              )}

              {content.leadScore.talking_points && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">Talking Points:</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {content.leadScore.talking_points.map((point: string, idx: number) => (
                      <li key={idx} className="flex items-start">
                        <span className="mr-1">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AISalesAssistant;
